import { useCallback, useMemo } from 'react'
import {
  ColDef,
  ColGroupDef,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  MenuItemDef,
} from 'ag-grid-community'
import {
  columnTypes,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import { OrganizationWorkingDayCalendarDetail } from '../../../../lib/functions/organizationWorkingDayCalendar'
import { dateV2Service } from '../../../../domain/value-object/DateV2'
import {
  DayOfWeek,
  TimeGrain,
  mapDayOfWeekToNumber,
} from '../../../../lib/functions/report'
import {
  SprintReportGroupKey,
  SprintReportKey,
} from '../../../../lib/functions/sprintReport'
import { WorkLoadUnitState } from '../../../hooks/useWorkloadUnit'
import { SprintReportRow } from '../model/sprintReportRow'
import { SprintReportPageState } from '../hooks/pageState'
import { colorPalette } from '../../../style/colorPalette'
import { SprintReportSearchCondition } from '../hooks/searchCondition'
import { defaultColumns } from './defaultColumns'
import {
  createDateReportColumnDef,
  createSprintReportColumnDef,
  createTotalReportColumnDef,
} from './reportValueColumns'
import { createSprintReportContextMenuItems } from './contextMenu'
import { SprintOption } from '../hooks/sprintOptions'

const TOTAL_ESTIMATED_HOUR_BG = colorPalette.monotone[1]

const grainStep = (
  grain: TimeGrain
): { amount: number; unit: 'day' | 'week' | 'month' } => {
  switch (grain) {
    case TimeGrain.DAY:
      return { amount: 1, unit: 'day' }
    case TimeGrain.WEEK:
      return { amount: 1, unit: 'week' }
    case TimeGrain.TWO_WEEKS:
      return { amount: 2, unit: 'week' }
    case TimeGrain.MONTH:
      return { amount: 1, unit: 'month' }
    default:
      return { amount: 1, unit: 'week' }
  }
}

const advanceDate = (date: Date, grain: TimeGrain): Date => {
  const step = grainStep(grain)
  return step.unit === 'day'
    ? dateV2Service.addDays(date, step.amount)
    : step.unit === 'week'
      ? dateV2Service.addDays(date, step.amount * 7)
      : dateV2Service.addMonths(date, step.amount)
}

// 列の開始境界を timeGrain と startDayOfWeek に合わせて調整する。
// 旧 BulkSheet 版と同じく、WEEK/TWO_WEEKS は startDayOfWeek の曜日まで遡り、MONTH は月初に揃える。
const alignStart = (
  start: Date,
  grain: TimeGrain,
  startDayOfWeek: DayOfWeek
): Date => {
  if (grain === TimeGrain.WEEK || grain === TimeGrain.TWO_WEEKS) {
    // Date#getDay は Sun=0..Sat=6、mapDayOfWeekToNumber は Mon=1..Sun=7 系のため
    // 差分を mod 7 で合わせて開始曜日まで遡る
    const dayOfWeek = start.getDay()
    const startDayOfWeekNum = mapDayOfWeekToNumber(startDayOfWeek)
    const fromDiff = (dayOfWeek - startDayOfWeekNum + 7) % 7
    return dateV2Service.addDays(start, -fromDiff)
  }
  if (grain === TimeGrain.MONTH) {
    return dateV2Service.startOfMonth(start)
  }
  return start
}

// SPRINT モードのスプリント列候補を集める。
// - 両端指定: [startSprint.startDate, endSprint.endDate] に完全に収まる sprint を列にする
// - 開始のみ または 終了のみ: その 1 スプリントだけを列にする (片側でも範囲が確定する設計にすると
//   開始のみで「以降全部」が出てしまい列爆発・ノイズになるため、ピンポイント解釈にする)
const collectSprintsInRange = (
  searchCondition: SprintReportSearchCondition,
  sprintOptions: SprintOption[]
): SprintOption[] => {
  const { startSprintUuid, endSprintUuid, teamUuid } = searchCondition
  if (!startSprintUuid && !endSprintUuid) return []
  const start = startSprintUuid
    ? sprintOptions.find(o => o.value === startSprintUuid)
    : undefined
  const end = endSprintUuid
    ? sprintOptions.find(o => o.value === endSprintUuid)
    : undefined
  // 指定された側の sprint が options に見つからない場合は表示できないので空を返す
  if (startSprintUuid && !start) return []
  if (endSprintUuid && !end) return []
  // 片側だけ指定: その 1 スプリントだけを列にする
  if (start && !end) return [start]
  if (end && !start) return [end]
  // 両端指定: 範囲内の sprint を列にする
  const scoped = teamUuid
    ? sprintOptions.filter(o => o.teamUuid === teamUuid)
    : sprintOptions
  return scoped
    .filter(o => o.startDate >= start!.startDate && o.endDate <= end!.endDate)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
}

const buildColumnDefs = (
  searchCondition: SprintReportSearchCondition,
  workingDayCalendars: OrganizationWorkingDayCalendarDetail[],
  sprintOptions: SprintOption[]
): (ColDef | ColGroupDef)[] => {
  // GroupKey が SPRINT の場合は SPRINT 列モード: 期間ベースではなく、スプリント単位に列を生成する。
  if (searchCondition.groupKey === SprintReportGroupKey.SPRINT) {
    const sprints = collectSprintsInRange(searchCondition, sprintOptions)
    if (sprints.length === 0) {
      return [...defaultColumns]
    }
    const startDate = dateV2Service.fromString(sprints[0].startDate)
    const endDate = dateV2Service.fromString(
      sprints[sprints.length - 1].endDate
    )
    if (!startDate || !endDate) {
      return [...defaultColumns]
    }
    const totalColumn = createTotalReportColumnDef(
      { from: startDate, to: endDate },
      searchCondition,
      workingDayCalendars
    )
    const sprintColumns = sprints.map(sprint =>
      createSprintReportColumnDef(sprint, searchCondition, workingDayCalendars)
    )
    return [...defaultColumns, totalColumn, ...sprintColumns]
  }

  const { dateTerm } = searchCondition
  if (!dateTerm.startDate || !dateTerm.endDate) {
    return [...defaultColumns]
  }

  const rawStart = dateV2Service.fromNumber(dateTerm.startDate)
  const endDate = dateV2Service.fromNumber(dateTerm.endDate)
  if (!rawStart || !endDate) {
    return [...defaultColumns]
  }
  const startDate = alignStart(
    rawStart,
    searchCondition.timeGrain,
    searchCondition.startDayOfWeek
  )

  const dateColumns: (ColDef | ColGroupDef)[] = []
  let cursor = startDate
  while (cursor < endDate) {
    const next = advanceDate(cursor, searchCondition.timeGrain)
    dateColumns.push(
      createDateReportColumnDef(
        { from: cursor, to: next },
        searchCondition,
        workingDayCalendars
      )
    )
    cursor = next
  }

  const totalColumn = createTotalReportColumnDef(
    { from: startDate, to: cursor },
    searchCondition,
    workingDayCalendars
  )

  return [...defaultColumns, totalColumn, ...dateColumns]
}

type SprintReportGridOptionsApi = {
  gridOptions: GridOptions<SprintReportRow>
  refreshColumns: (
    searchCondition: SprintReportSearchCondition,
    workingDayCalendars: OrganizationWorkingDayCalendarDetail[],
    sprintOptions: SprintOption[]
  ) => void
}

export const useSprintReportGridOptions = (
  getContextMenuArgs: () => SprintReportSearchCondition,
  gridApi: GridApi | null,
  initialPageState: SprintReportPageState,
  initialWorkloadUnitState: WorkLoadUnitState
): SprintReportGridOptionsApi => {
  // 動的カラム更新は ag-grid v33+ 以降 setGridOption('columnDefs', ...) で行う必要があるため、
  // gridOptions 自体は再生成せず（参照を安定させ）、列定義は API 経由で差し替える。
  // pageState/workloadUnitState の値は親側の useEffect で context オブジェクトを mutate して反映する。
  const gridOptions: GridOptions<SprintReportRow> = useMemo(
    () => ({
      context: {
        pageState: initialPageState,
        workloadUnitState: initialWorkloadUnitState,
      },
      columnTypes: columnTypes(),
      components: frameworkComponents,
      defaultColDef: {
        sortable: false,
        filter: false,
        resizable: true,
        suppressHeaderMenuButton: true,
      },
      columnDefs: [...defaultColumns],
      onCellClicked: defaultOnCellClicked,
      // 各メンバーの「合計」行（TOTAL_ESTIMATED_HOUR）は背景を薄グレーにして集計行であることを示す
      getRowStyle: params => {
        if (params.data?.key === SprintReportKey.TOTAL_ESTIMATED_HOUR) {
          return { backgroundColor: TOTAL_ESTIMATED_HOUR_BG }
        }
        return undefined
      },
      getContextMenuItems: (
        params: GetContextMenuItemsParams<SprintReportRow>
      ): MenuItemDef[] =>
        createSprintReportContextMenuItems(params, getContextMenuArgs()),
    }),
    // 意図的に初期値のみで生成。pageState/workloadUnitState 更新は context mutate で反映する
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getContextMenuArgs]
  )

  const refreshColumns = useCallback(
    (
      searchCondition: SprintReportSearchCondition,
      workingDayCalendars: OrganizationWorkingDayCalendarDetail[],
      sprintOptions: SprintOption[]
    ) => {
      const newDefs = buildColumnDefs(
        searchCondition,
        workingDayCalendars,
        sprintOptions
      )
      gridApi?.setGridOption('columnDefs', newDefs)
    },
    [gridApi]
  )

  return { gridOptions, refreshColumns }
}
